import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus } from 'lucide-react';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { formatDate } from '../lib/format';
import { ListShell } from '../components/ListShell';
import { Button } from '../components/ui/Button';
import { DataTable } from '../components/ui/DataTable';
import { EmptyState } from '../components/ui/EmptyState';
import { Pagination } from '../components/people/Pagination';
import {
  CycleDrawer,
  CycleStatusChip,
  CycleTypeChip,
  canEditCycleMetadata,
  type CycleDrawerMode,
  type ExamCycleListItem,
} from '../components/exams';
import { EditIconButton } from '../components/shared/EditIconButton';
import { GatedSection } from '../components/shared/GatedSection';
import { ListOrOutlet } from '../components/shared/ListOrOutlet';
import {
  FilterBar,
  SortableHeader,
  useListFilters,
  type FilterRoute,
} from '../components/filters';
import {
  examsFilterConfig,
  examsSearchSchema,
  EXAMS_SORT_COLUMNS,
} from '../components/exams/examsFilterConfig';
import type { ExamCycleListResponse } from '@bbl/validation';

const PAGE_SIZE = 20;

export const Route = createFileRoute('/_authed/academics/exams')({
  validateSearch: examsSearchSchema,
  component: ExamsListPage,
});

function ExamsListPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const hasRole = useAuthStore((s) => s.hasRole);
  const canManage = hasRole('super_admin', 'academic_admin');

  const [drawer, setDrawer] = useState<CycleDrawerMode | null>(null);

  // The single source of truth for search / filters / sort / page, backed by
  // the route's URL search params. The adapter narrows the route's precisely-
  // typed search/navigate surface to the toolkit's structural `FilterRoute`
  // contract; the route's own Zod `validateSearch` keeps the boundary typed.
  const filterRoute: FilterRoute = {
    useSearch: () => Route.useSearch() as Record<string, unknown>,
    useNavigate: () => {
      const navigateRoute = Route.useNavigate();
      return (opts) =>
        navigateRoute({
          search: ((prev: Record<string, unknown>) => opts.search(prev)) as never,
          ...(opts.replace !== undefined ? { replace: opts.replace } : {}),
        });
    },
  };
  const filters = useListFilters(examsFilterConfig, filterRoute, {
    pageSize: PAGE_SIZE,
  });
  const { values, apiParams, setSort, setPage } = filters;

  // `academicYear` stays a sibling single-value control (the backend filters it
  // with a single exact `eq`, so it can't be a comma-joined toolkit facet). It
  // lives in the same URL search contract (`examsSearchSchema`), so read/write
  // it there — via the route's own typed `useSearch`/`useNavigate` — and fold it
  // into the one query alongside `apiParams`. Kept as a free-text input (not a
  // select) so arbitrary year strings like "2026-2027" can be entered.
  const yearFilter = Route.useSearch().academicYear ?? '';
  const routeNavigate = Route.useNavigate();
  const setYearFilter = (next: string) => {
    void routeNavigate({
      search: (prev) => ({
        ...prev,
        academicYear: next.trim() || undefined,
        // Changing a filter resets pagination — mirror the toolkit's behaviour.
        page: undefined,
      }),
    });
  };

  const listQuery = useQuery({
    queryKey: ['exams', 'cycles', apiParams, yearFilter] as const,
    queryFn: ({ signal }) =>
      apiRequest<ExamCycleListResponse>('/api/exams/cycles', {
        signal,
        query: {
          ...apiParams,
          ...(yearFilter.trim() ? { academicYear: yearFilter.trim() } : {}),
        },
      }),
    placeholderData: (prev) => prev,
    retry: (failureCount, err) => {
      if (err instanceof ApiError && err.status === 404) return false;
      return failureCount < 2;
    },
  });

  const columns = useMemo<ColumnDef<ExamCycleListItem, unknown>[]>(
    () => {
      const base: ColumnDef<ExamCycleListItem, unknown>[] = [
      {
        id: 'name',
        accessorKey: 'name',
        enableSorting: false,
        header: 'Name',
        cell: (info) => {
          const row = info.row.original;
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                void navigate({
                  to: '/academics/exams/$cycleId',
                  params: { cycleId: row.id },
                });
              }}
              className="text-left font-medium text-[color:var(--color-text)] hover:text-gold-700"
            >
              {row.name}
            </button>
          );
        },
      },
      {
        id: 'type',
        accessorKey: 'type',
        enableSorting: false,
        header: 'Type',
        cell: (info) => <CycleTypeChip type={info.row.original.type} />,
      },
      {
        id: 'academicYear',
        accessorKey: 'academicYear',
        enableSorting: false,
        header: 'Year',
        cell: (info) => (
          <span className="text-[color:var(--color-text-muted)]">
            {info.row.original.academicYear}
          </span>
        ),
      },
      {
        id: 'term',
        accessorKey: 'term',
        enableSorting: false,
        header: 'Term',
        cell: (info) => (
          <span className="text-xs text-[color:var(--color-text-muted)]">
            {info.row.original.term ?? '—'}
          </span>
        ),
      },
      {
        id: 'dates',
        // The URL-backed `SortableHeader` is the single source of truth for
        // sorting; disable DataTable's built-in (client-side) column sort so it
        // doesn't add a competing handler + duplicate sort glyph on this header.
        enableSorting: false,
        header: () => (
          <SortableHeader
            label="Dates"
            binding={EXAMS_SORT_COLUMNS.startDate}
            currentSort={values.sort}
            onSort={setSort}
          />
        ),
        cell: (info) => {
          const r = info.row.original;
          return (
            <span className="text-xs text-[color:var(--color-text-muted)]">
              {formatDate(`${r.startDate}T00:00:00Z`)} – {formatDate(`${r.endDate}T00:00:00Z`)}
            </span>
          );
        },
      },
      {
        id: 'status',
        accessorKey: 'status',
        enableSorting: false,
        header: 'Status',
        cell: (info) => <CycleStatusChip status={info.row.original.status} />,
      },
      {
        id: 'subjectCount',
        enableSorting: false,
        header: 'Subjects',
        cell: (info) => (
          <span className="tabular-nums text-[color:var(--color-text-muted)]">
            {info.row.original.subjectCount}
          </span>
        ),
      },
      {
        id: 'marksRecordedCount',
        enableSorting: false,
        header: 'Marks recorded',
        cell: (info) => (
          <span className="tabular-nums text-[color:var(--color-text-muted)]">
            {info.row.original.marksRecordedCount}
          </span>
        ),
      },
      ];

      if (canManage) {
        base.push({
          id: 'actions',
          enableSorting: false,
          header: '',
          cell: (info) => {
            const row = info.row.original;
            const editable = canEditCycleMetadata(row.status);
            return (
              <div className="flex justify-end">
                <EditIconButton
                  label={`Edit ${row.name}`}
                  title={
                    editable
                      ? `Edit ${row.name}`
                      : 'Archived cycles cannot be edited.'
                  }
                  disabled={!editable}
                  onClick={() => setDrawer({ kind: 'edit', cycle: row })}
                />
              </div>
            );
          },
        });
      }

      return base;
    },
    [navigate, canManage, values.sort, setSort],
  );

  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const isLoadingList = listQuery.isLoading && !listQuery.data;

  return (
    <ListOrOutlet basePath="/academics/exams">
    <ListShell
      title="Exams & marks"
      description="Plan exam cycles, record marks, and publish results."
      primaryAction={
        canManage ? (
          <Button
            onClick={() => setDrawer({ kind: 'create' })}
            icon={<Plus size={16} aria-hidden="true" />}
          >
            Create cycle
          </Button>
        ) : null
      }
    >
      <FilterBar config={examsFilterConfig} filters={filters} resultCount={total} />

      <div className="mb-4 max-w-xs">
        <label
          htmlFor="academic-year-filter"
          className="flex items-baseline justify-between text-sm font-medium"
        >
          <span>Academic year</span>
          <span className="text-xs text-[color:var(--color-text-subtle)]">Optional</span>
        </label>
        <input
          id="academic-year-filter"
          type="search"
          value={yearFilter}
          onChange={(e) => setYearFilter(e.target.value)}
          placeholder="e.g. 2026-2027"
          className="mt-1 w-full rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 py-2 text-sm focus:border-gold-500 focus:outline-none focus:ring-1 focus:ring-gold-500"
        />
      </div>

      <GatedSection
        error={listQuery.error}
        title="Exams & marks"
        onRetry={() => listQuery.refetch()}
      >
        <DataTable<ExamCycleListItem>
          data={items}
          columns={columns}
          isLoading={isLoadingList}
          getRowId={(row) => row.id}
          onRowClick={(row) =>
            void navigate({
              to: '/academics/exams/$cycleId',
              params: { cycleId: row.id },
            })
          }
          emptyState={
            <EmptyState
              title="No exam cycles match these filters"
              description={
                canManage
                  ? 'Try clearing a filter, or create the first exam cycle to start tracking subjects and marks.'
                  : 'Once academic admins create a cycle, it will appear here.'
              }
              action={
                canManage ? (
                  <Button
                    onClick={() => setDrawer({ kind: 'create' })}
                    icon={<Plus size={16} aria-hidden="true" />}
                  >
                    Create cycle
                  </Button>
                ) : null
              }
            />
          }
        />
        <Pagination
          page={values.page}
          pageSize={PAGE_SIZE}
          total={total}
          onPageChange={setPage}
        />
      </GatedSection>

      <CycleDrawer
        mode={drawer}
        onClose={() => setDrawer(null)}
        onSaved={(cycleId) => {
          const wasCreate = drawer?.kind === 'create';
          setDrawer(null);
          void queryClient.invalidateQueries({ queryKey: ['exams', 'cycles'] });
          if (wasCreate) {
            void navigate({
              to: '/academics/exams/$cycleId',
              params: { cycleId },
            });
          }
        }}
      />
    </ListShell>
    </ListOrOutlet>
  );
}
